/* The variant choices (scent tiles, size rows): one option, its group, and the whole selection.
   Shared by the product page (template-product.js, ProductForm) and the quick-add panel (quick-add.js).
   The owner of the selection gives it variantsByTitleJSON and listens for "variant-selection:changed". */
// In a block, and only once: the PDP and the quick-add panel both load this file.
if (!customElements.get("variant-option")) {
  class VariantOption extends HTMLElement {
    constructor() {
      super();
      this.changeEvent = new CustomEvent("variant-option:changed", {
        bubbles: true,
      });
      this.isActive = this.classList.contains("radio-active");
      this._value = this.getAttribute("value");
    }

    setActive() {
      if (!this.isActive) {
        this.isActive = true;
        this.classList.add("radio-active");
        this.setAttribute("aria-checked", "true");
        this.tabIndex = 0;
        this.dispatchEvent(this.changeEvent);
      }
    }

    removeActive() {
      this.isActive = false;
      this.classList.remove("radio-active");
      this.setAttribute("aria-checked", "false");
      this.tabIndex = -1;
    }

    // Keyboard, as a radio group: Tab reaches the chosen option, the arrow keys move the choice, Enter/Space choose.
    onKey(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.setActive();
        return;
      }
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      // The options can have been rendered anew under the key press (a variant change re-renders them).
      const group = this.closest("variant-option-group") || this.parentElement;
      if (!group) return;
      // Only the options in view (the shade tabs can hide some).
      const all = [...group.querySelectorAll("variant-option")].filter((node) => node === this || node.getClientRects().length);
      const next = all[(all.indexOf(this) + step + all.length) % all.length];
      if (next && next !== this) {
        next.focus();
        next.setActive();
      }
    }

    connectedCallback() {
      this.setAttribute("role", "radio");
      this.setAttribute("aria-checked", this.isActive ? "true" : "false");
      this.tabIndex = this.isActive ? 0 : -1;
      this.addEventListener("click", this.setActive);
      this.addEventListener("keydown", this.onKey);
      // A group with nothing chosen still needs one Tab stop.
      queueMicrotask(() => {
        const group = this.closest("variant-option-group");
        if (group && !group.querySelector('variant-option[tabindex="0"]')) {
          const first = group.querySelector("variant-option");
          if (first) first.tabIndex = 0;
        }
      });
    }
  }

  // A dropdown over an option's buttons (the gift card's amounts, component-variant-selection.liquid): choosing in it
  // chooses the matching button, as a tap would.
  document.addEventListener("change", (event) => {
    const select = event.target.closest && event.target.closest("select[data-variant-dropdown]");
    if (!select) return;
    const group = select.closest("variant-option-group");
    const option = group && [...group.querySelectorAll("variant-option")].find((node) => node.getAttribute("value") === select.value);
    if (option) option.setActive();
  });

  class VariantOptionGroup extends HTMLElement {
    constructor() {
      super();
      this.changeEvent = new CustomEvent("variant-option-group:changed", {
        bubbles: true,
      });
      this.activeNode = this.querySelector(".radio-active");
    }

    cacheOptions() {
      this.options = {};

      Array.from(this.querySelectorAll("variant-option")).forEach((node) => {
        this.options[node._value] = {
          element: node,
          value: node._value,
        };
      });
    }

    catchVariantChange() {
      this.addEventListener("variant-option:changed", (event) => {
        // Every option but the one just chosen: with the arrow keys the new one can come before the old one.
        this.querySelectorAll(".radio-active").forEach((node) => {
          if (node !== event.target) node.removeActive();
        });

        this.activeNode = event.target;
        this.dispatchEvent(this.changeEvent);
      });
    }

    connectedCallback() {
      this.cacheOptions();
      this.catchVariantChange();
      // Named by its option ("Duft", "Størrelse"): the visible label also carries the chosen value.
      this.setAttribute("role", "radiogroup");
      const label = this.querySelector(".variants-label");
      if (label && !this.hasAttribute("aria-label")) this.setAttribute("aria-label", label.textContent.split(":")[0].trim());
    }
  }

  class VariantSelection extends HTMLElement {
    constructor() {
      super();
      this.changeEvent = new CustomEvent("variant-selection:changed", {
        bubbles: true,
      });
      this.variantGroupOptions = Array.from(
        this.querySelectorAll("variant-option-group")
      );
      this.activeVariantTitle;
      this.activeGroups = [];
    }

    registerOptionChange() {
      this.addEventListener("variant-option-group:changed", (event) => {
        this.buildActiveVariantTitle();

        if (!this.variantsByTitleJSON[this.activeVariantTitle]) {
          var lastUpdatedOptionName = event.target.getAttribute("name");
          var hasUpdatedOneOption = false;
          this.variantGroupOptions.forEach((elem) => {
            if (elem.getAttribute("name") != lastUpdatedOptionName) {
              if (!hasUpdatedOneOption) {
                elem.querySelector("variant-option").click();
                hasUpdatedOneOption = true;
              }
            }
          });
          return;
        }

        this.dispatchEvent(this.changeEvent);
      });
    }

    // From the DOM when a group has not set its activeNode yet: inserted later (the quick-add panel), the selection
    // connects before its groups.
    buildActiveVariantTitle() {
      this.activeVariantTitle = this.variantGroupOptions
        .map((elem) => {
          const node = elem.activeNode || elem.querySelector("variant-option.radio-active");
          return node ? node._value || node.getAttribute("value") : "";
        })
        .join(" / ");
    }

    updateId(id) {
      this.querySelector('input[name="id"]').value = id;
      var url = new URL(window.location.href);

      if (id) {
        url.searchParams.set("variant", id);
      } else {
        url.searchParams.delete("variant");
      }

      window.history.replaceState({}, "", url.href);
    }

    connectedCallback() {
      this.registerOptionChange();
      this.buildActiveVariantTitle();
      this.dispatchEvent(this.changeEvent);
    }
  }

  customElements.define("variant-option", VariantOption);
  customElements.define("variant-option-group", VariantOptionGroup);
  customElements.define("variant-selection", VariantSelection);
}
